import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import BackOfficeLayout from "../../../components/backoffice-layout";
import InputSearchComponent from "../../../components/input-search";
import PaginatorComponent from "../../../components/paginator";
import SearchableField from "../../../components/searchable-field";
import DateRangePickerComponent from "../../../components/date-range-picker";
import LoadingModal from "../../../components/loading-modal";
import CoreAPI from "../../../services/core";
import { useOpenModalTokenExpired } from "../../../contexts";
import { formatDateByString } from "../../../utils/default";
import IconArrow from "../../../assets/images/icon-arrow-2.svg";

const Container = styled.div`
    width: calc(100% - 32px);
    background-color: #FFFFFF;
    padding: 40px 16px;
    border-radius: 16px;

    .header-box {
        display: flex;
        justify-content: space-between;
        margin-bottom: 24px;

        .header-label {
            width: fit-content;
            color: #25368D;
            font-size: 20px;
            font-weight: 600;
            line-height: 29.9px;
            text-align: left;
            padding: 2px;
            border-width: 0px 0px 3px 0px;
            border-style: solid;
            border-color: #25368D;
        }
    }

    .filter-bar {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 36px;

        .filters-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 16px;
            align-items: end;
        }
    }

    .preset-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;

        .preset-label {
            color: #505050;
            font-size: 13px;
            font-weight: 500;
            margin-right: 4px;
        }

        .preset-btn {
            padding: 6px 14px;
            border-radius: 20px;
            border: 1px solid #E5E5E5;
            background-color: #FFFFFF;
            color: #505050;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                border-color: #25368D;
                color: #25368D;
            }

            &.active {
                background-color: #25368D;
                color: #FFFFFF;
                border-color: #25368D;
            }
        }
    }

    .data-box {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
`;

const Table = styled.div`
    width: 100%;
    border: 1px solid #F2F2F2;
    border-radius: 12px;
    overflow: hidden;

    .row {
        display: grid;
        grid-template-columns: 60px 2fr 1.5fr 1fr 1fr 1.5fr 60px;
        align-items: center;
        padding: 14px 16px;
        border-bottom: 1px solid #F2F2F2;
        font-size: 14px;
        color: #2A2A2A;
        transition: background-color 0.2s ease;

        &.header {
            background-color: #FBE25033;
            color: #25368D;
            font-weight: 600;
        }

        &.data {
            cursor: pointer;
            &:hover {
                background-color: #FBFBFE;
            }
        }

        &:last-child {
            border-bottom: none;
        }
    }

    .cell-count {
        font-weight: 600;
        color: #25368D;
    }

    .expand {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 14px;
            height: 14px;
            transition: transform 0.2s ease;
        }

        &.open img {
            transform: rotate(90deg);
        }
    }

    .detail {
        background-color: #FBFBFE;
        padding: 16px 24px 16px 76px;
        border-bottom: 1px solid #F2F2F2;

        .detail-title {
            color: #25368D;
            font-size: 13px;
            font-weight: 600;
            margin-bottom: 8px;
        }

        .detail-row {
            display: grid;
            grid-template-columns: 3fr 1fr 1fr 2fr;
            padding: 8px 0px;
            font-size: 13px;
            color: #505050;
            border-bottom: 1px dashed #EAEAEA;

            &:last-child { border-bottom: none; }

            &.header {
                color: #25368D;
                font-weight: 600;
                font-size: 12px;
                border-bottom: 1px solid #E5E5E5;
            }
        }

        .empty {
            font-size: 13px;
            color: #B5B5B5;
            padding: 8px 0px;
        }
    }

    .empty-box {
        padding: 48px 16px;
        text-align: center;
        color: #B5B5B5;
        font-size: 14px;
    }
`;

function rangeForPreset(preset) {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    switch (preset) {
        case "this_month":
            return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0) };
        case "last_month":
            return { start: new Date(y, m - 1, 1), end: new Date(y, m, 0) };
        case "this_year":
            return { start: new Date(y, 0, 1), end: new Date(y, 11, 31) };
        case "last_year":
            return { start: new Date(y - 1, 0, 1), end: new Date(y - 1, 11, 31) };
        default:
            return { start: null, end: null };
    }
}

function ActivityPage() {
    const navigate = useNavigate();
    const { setOpenModalTokenExpired } = useOpenModalTokenExpired();

    const [search, setSearch] = useState("");
    const [listForm, setListForm] = useState([]);
    const [listFormSelect, setListFormSelect] = useState([]);
    const [listProvince, setListProvince] = useState([]);
    const [listProvinceSelect, setListProvinceSelect] = useState([]);
    const [dateRange, setDateRange] = useState(() => rangeForPreset("this_month"));
    const [activePreset, setActivePreset] = useState("this_month");

    const [pageOption, setPageOption] = useState({
        limit: 20,
        page: 1,
        total_item: 0,
        total_page: 1,
    });
    const [listData, setListData] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [detailCache, setDetailCache] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        CoreAPI.evaluatorHistoryHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        CoreAPI.evaluationFormHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        CoreAPI.locationHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        onInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onInit() {
        let _profile = localStorage.getItem("profile");
        try { _profile = JSON.parse(_profile); } catch { }
        if (_profile?.user_username !== "superadmin" && _profile?.user_username !== "admin") {
            navigate(`/account`);
            return;
        }
        setIsLoading(true);
        await Promise.all([onLoadForms(), onLoadProvinces()]);
        const initialRange = rangeForPreset("this_month");
        await onFetch({
            limit: pageOption.limit, page: 1,
            search: "", formId: "", province: "",
            start: initialRange.start, end: initialRange.end,
        });
        setIsLoading(false);
    }

    async function onLoadForms() {
        let response = null;
        try {
            response = await CoreAPI.evaluationFormHttpService.evaluationFormGetListByParam({
                search: "",
                search_by: ["evaluation_name"],
                order: "ASC",
                order_by: "evaluation_name",
                limit: 1000, page: 1,
                type: [], department: [], division: [], province: [], location: [],
            });
        } catch { }
        const rows = Array.isArray(response?.data) ? response.data : [];
        setListForm(rows.map(r => ({
            id: r.evaluation_form_evaluation_form_id,
            label: r.evaluation_form_evaluation_name || "-",
        })));
    }

    async function onLoadProvinces() {
        let response = [];
        try {
            response = await CoreAPI.locationHttpService.locationProvince();
        } catch { }
        const rows = Array.isArray(response) ? response.map(x => ({
            id: x.location_province,
            label: x.location_province,
        })) : [];
        setListProvince(rows);
    }

    function toStartIso(d) {
        if (!d) return null;
        const x = new Date(d); x.setHours(0, 0, 0, 0); return x.toISOString();
    }
    function toEndIso(d) {
        if (!d) return null;
        const x = new Date(d); x.setHours(23, 59, 59, 999); return x.toISOString();
    }

    async function onFetch({ limit, page, search, formId, province, start, end }) {
        let response = null;
        try {
            response = await CoreAPI.evaluatorHistoryHttpService.activityByParam({
                limit, page,
                search: search || "",
                evaluation_form_id: formId || "",
                province: province || "",
                start_date: toStartIso(start),
                end_date: toEndIso(end),
            });
        } catch { }
        if (response) {
            setPageOption({
                limit: response.limit,
                page: response.page,
                total_item: response.total_item,
                total_page: response.total_page,
            });
            setListData(Array.isArray(response.data) ? response.data : []);
        }
        // reset expanded state when list changes
        setExpandedId(null);
        setDetailCache({});
    }

    function buildFilterParams(overrides = {}) {
        return {
            limit: pageOption.limit,
            page: pageOption.page,
            search,
            formId: (listFormSelect[0]?.id) || "",
            province: (listProvinceSelect[0]?.label) || "",
            start: dateRange.start,
            end: dateRange.end,
            ...overrides,
        };
    }

    async function handleSearch() {
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1 }));
        setIsLoading(false);
    }

    async function handleInputSearch(param) {
        setSearch(param);
    }

    async function handleChangeForm(picked) {
        setListFormSelect(picked);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, formId: (picked[0]?.id) || "" }));
        setIsLoading(false);
    }

    async function handleChangeProvince(picked) {
        setListProvinceSelect(picked);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, province: (picked[0]?.label) || "" }));
        setIsLoading(false);
    }

    async function handleChangeDateRange(v) {
        setDateRange(v);
        setActivePreset(null);
        if (v.start && v.end) {
            setIsLoading(true);
            await onFetch(buildFilterParams({ page: 1, start: v.start, end: v.end }));
            setIsLoading(false);
        } else if (!v.start && !v.end) {
            setIsLoading(true);
            await onFetch(buildFilterParams({ page: 1, start: null, end: null }));
            setIsLoading(false);
        }
    }

    async function handlePreset(preset) {
        const range = rangeForPreset(preset);
        setDateRange(range);
        setActivePreset(preset);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, start: range.start, end: range.end }));
        setIsLoading(false);
    }

    async function handleChangePageOption(param) {
        setIsLoading(true);
        await onFetch(buildFilterParams({ limit: param.limit, page: param.page }));
        setIsLoading(false);
    }

    async function handleToggleExpand(evaluatorId) {
        if (expandedId === evaluatorId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(evaluatorId);
        if (detailCache[evaluatorId]) return;
        let response = null;
        try {
            response = await CoreAPI.evaluatorHistoryHttpService.activityDetail({
                evaluator_id: evaluatorId,
                start_date: toStartIso(dateRange.start),
                end_date: toEndIso(dateRange.end),
            });
        } catch { }
        setDetailCache(prev => ({
            ...prev,
            [evaluatorId]: Array.isArray(response?.data) ? response.data : [],
        }));
    }

    function renderRow(row, index) {
        const displayIndex = (pageOption.page - 1) * pageOption.limit + index + 1;
        const name = [row.firstname, row.lastname].filter(Boolean).join(" ") || "-";
        const isOpen = expandedId === row.evaluator_id;
        const detail = detailCache[row.evaluator_id];

        return (
            <React.Fragment key={row.evaluator_id}>
                <div className="row data" onClick={() => handleToggleExpand(row.evaluator_id)}>
                    <div>{displayIndex}</div>
                    <div>{name}</div>
                    <div>{row.province || "-"}</div>
                    <div className="cell-count">{row.submission_count}</div>
                    <div className="cell-count">{row.service_count || 0}</div>
                    <div>{row.last_submitted_at ? formatDateByString(row.last_submitted_at, "DD/MM/YYYY HH:mm") : "-"}</div>
                    <div className={`expand ${isOpen ? "open" : ""}`}>
                        <img alt="expand" src={IconArrow} />
                    </div>
                </div>
                {isOpen && (
                    <div className="detail">
                        <div className="detail-title">แบบสอบถามที่ตอบ</div>
                        {!detail && <div className="empty">กำลังโหลด...</div>}
                        {detail && detail.length === 0 && <div className="empty">ไม่มีข้อมูล</div>}
                        {detail && detail.length > 0 && (
                            <div className="detail-row header">
                                <div>แบบสอบถาม</div>
                                <div>ครั้งที่ตอบ</div>
                                <div>ครั้งที่เข้าใช้บริการ</div>
                                <div>ล่าสุด</div>
                            </div>
                        )}
                        {detail && detail.map(d => {
                            const latest = d.last_attended_at && (!d.last_submitted_at || new Date(d.last_attended_at) > new Date(d.last_submitted_at))
                                ? d.last_attended_at : d.last_submitted_at;
                            return (
                                <div className="detail-row" key={d.evaluation_form_id}>
                                    <div>{d.evaluation_name || "-"}</div>
                                    <div>{d.attempts}</div>
                                    <div>{d.service_count || 0}</div>
                                    <div>{latest ? formatDateByString(latest, "DD/MM/YYYY HH:mm") : "-"}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </React.Fragment>
        );
    }

    return (
        <BackOfficeLayout>
            <LoadingModal open={isLoading} />
            <Container>
                <div className="header-box vertical-center">
                    <div className="header-label">
                        ประวัติการตอบแบบสอบถาม {pageOption.total_item !== 0 ? `(${pageOption.total_item})` : ""}
                    </div>
                </div>
                <div className="filter-bar">
                    <InputSearchComponent
                        styleInput={{ height: "56px" }}
                        placeholder={"ค้นหา ชื่อ นามสกุล หรือ LINE user id"}
                        onEnter={handleSearch}
                        onInput={handleInputSearch}
                    />
                    <div className="filters-row">
                        <SearchableField
                            label={"แบบสอบถาม"}
                            placeholder={"ทั้งหมด — พิมพ์เพื่อค้นหา"}
                            options={listForm}
                            value={listFormSelect[0] || null}
                            onChange={(picked) => handleChangeForm(picked ? [picked] : [])}
                        />
                        <SearchableField
                            label={"จังหวัด"}
                            placeholder={"ทั้งหมด — พิมพ์เพื่อค้นหา"}
                            options={listProvince}
                            value={listProvinceSelect[0] || null}
                            onChange={(picked) => handleChangeProvince(picked ? [picked] : [])}
                        />
                        <DateRangePickerComponent
                            label={"ช่วงวันที่"}
                            placeholder={"เลือกช่วงวันที่"}
                            value={dateRange}
                            onChange={handleChangeDateRange}
                        />
                    </div>
                    <div className="preset-row">
                        <span className="preset-label">เลือกช่วงด่วน:</span>
                        {[
                            { id: "this_month", label: "เดือนนี้" },
                            { id: "last_month", label: "เดือนที่แล้ว" },
                            { id: "this_year", label: "ปีนี้" },
                            { id: "last_year", label: "ปีที่แล้ว" },
                        ].map(p => (
                            <button
                                key={p.id}
                                className={`preset-btn ${activePreset === p.id ? "active" : ""}`}
                                onClick={() => handlePreset(p.id)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="data-box">
                    <Table>
                        <div className="row header">
                            <div>#</div>
                            <div>ผู้ตอบแบบสอบถาม</div>
                            <div>จังหวัด</div>
                            <div>ครั้งที่ตอบ</div>
                            <div>ครั้งที่เข้าใช้บริการ</div>
                            <div>ตอบล่าสุด</div>
                            <div></div>
                        </div>
                        {listData.length === 0 && !isLoading && (
                            <div className="empty-box">ไม่พบข้อมูล</div>
                        )}
                        {listData.map(renderRow)}
                    </Table>
                    <PaginatorComponent
                        limit={pageOption.limit}
                        page={pageOption.page}
                        total_item={pageOption.total_item}
                        total_page={pageOption.total_page}
                        onChangePageOption={handleChangePageOption}
                    />
                </div>
            </Container>
        </BackOfficeLayout>
    );
}

export default ActivityPage;
